/**
 * Shared DTOs and Zod schemas for the people route.
 *
 * `people` is the one cross-cutting entity in the data model — Almanac
 * uses it for obligation subjects (birthdays, anniversaries) and Folio
 * will use it for photo tagging. The Almanac-side surface is kept
 * deliberately minimal; archive-specific fields can be added when
 * Folio actually needs them.
 *
 * Mirrors DATA_MODEL.md §people (subset).
 */

import { z } from 'zod';

export const personCreateSchema = z.object({
  id: z.string().min(8).max(64).optional(),
  displayName: z.string().min(1).max(200).trim(),
  relationship: z.string().max(200).nullish(),
  // Multiple "of …" links. Replaces the prior single-FK shape so
  // Oscar can be "son of both parents". Empty array clears them all;
  // absence (undefined) on update leaves them untouched.
  relatedPersonIds: z.array(z.string().min(1).max(64)).max(10).optional(),
  notes: z.string().max(4000).nullish(),
  // Explicit household membership — groups first in the People list and
  // leads every person picker (most documents are about the household).
  household: z.boolean().optional(),
});

export const personUpdateSchema = personCreateSchema.partial();

export type PersonCreateInput = z.infer<typeof personCreateSchema>;
export type PersonUpdateInput = z.infer<typeof personUpdateSchema>;

export interface PersonDTO {
  id: string;
  displayName: string;
  relationship: string | null;
  relatedPersonIds: string[];
  notes: string | null;
  household: boolean;
  /** Version token of the person's photo, or null when none is set.
   *  Changes only when the photo itself is replaced — NOT on profile
   *  edits — so the versioned avatar URL stays stable and the browser/
   *  service-worker caches keep serving it instantly. Served at
   *  GET /api/shared/people/:id/avatar. Initials when null. */
  avatarVersion: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
